import apiClient from './axios';

/**
 * CSV 일괄 업로드는 기본 10초(axios.js)로는 모자라다.
 * 2026-10-12 개발사 흔적 점검 D3: 큰 CSV 는 화면에 «실패»가 떠도 서버에서는 계속 들어가,
 * 다시 올리면 중복이 생겼다. 업로드만 5분까지 기다린다.
 */
export const CSV_UPLOAD_TIMEOUT_MS = 300_000;

/**
 * CSV 데이터 타입 정의
 *
 * KolsDB:
 * - 'kols-db' : KolsDB 인플루언서
 *
 * Campaign:
 * - 'campaign-influencer' : 캠페인 인플루언서탭
 * - 'campaign-delivery' : 캠페인 배송탭
 * - 'campaign-result' : 캠페인 결과탭
 *
 * Shipping:
 * - 'shipping-direct' : 쉬핑 다이렉트 테이블
 * - 'shipping-fulfillment-step1' : 쉬핑 풀필먼트 Step1 테이블
 * - 'shipping-fulfillment-step2' : 쉬핑 풀필먼트 Step2 테이블
 * - 'shipping-delivery' : 쉬핑 배송사 테이블
 *
 * My KOLs:
 * - 'my-kols' : My KOLs
 *
 * @typedef {'kols-db' | 'campaign-influencer' | 'campaign-delivery' | 'campaign-result' | 'shipping-direct' | 'shipping-fulfillment-step1' | 'shipping-fulfillment-step2' | 'shipping-delivery' | 'my-kols'} CSVDataType
 */

/**
 * 통합 CSV 업로드 API
 * POST /api/csv-upload
 *
 * @param {File} file - CSV 파일
 * @param {CSVDataType} dataType - 데이터 종류
 * @param {string} [targetId] - 대상 ID (campaignId, shippingId, myKolsId 등)
 * @returns {Promise} API 응답
 */
export const uploadCSV = async (file, dataType, targetId = null) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('dataType', dataType);
  if (targetId) {
    formData.append('targetId', targetId);
  }

  const response = await apiClient.post('/api/csv-upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: CSV_UPLOAD_TIMEOUT_MS,
  });
  return response.data;
};

/**
 * 통합 CSV 다운로드 API
 * GET /api/csv-download
 *
 * @param {CSVDataType} dataType - 데이터 종류
 * @param {Object} [options] - 다운로드 옵션
 * @param {string} [options.targetId] - 대상 ID (campaignId, shippingId, myKolsId)
 * @param {'template' | 'data'} [options.format='data'] - 다운로드 형식 (템플릿 또는 데이터)
 * @param {Object} [options.filters] - kols-db 전용 필터 파라미터
 * @param {string} [options.filters.search] - 검색어
 * @param {string} [options.filters.country] - 국가코드 (쉼표 구분)
 * @param {string} [options.filters.platformType] - 플랫폼 (쉼표 구분)
 * @param {string} [options.filters.contentType] - 콘텐츠 유형 (쉼표 구분)
 * @param {number} [options.filters.minFollowers] - 최소 팔로워
 * @param {number} [options.filters.maxFollowers] - 최대 팔로워
 * @param {number} [options.filters.minViews] - 최소 조회수
 * @param {number} [options.filters.maxViews] - 최대 조회수
 * @param {number} [options.filters.minLikes] - 최소 좋아요
 * @param {number} [options.filters.maxLikes] - 최대 좋아요
 * @param {string} [options.filters.kolCurrency] - KOL 통화
 * @param {number} [options.filters.kolMinPrice] - KOL 최소 단가
 * @param {number} [options.filters.kolMaxPrice] - KOL 최대 단가
 * @param {string} [options.filters.clientCurrency] - 클라이언트 통화
 * @param {number} [options.filters.clientMinPrice] - 클라이언트 최소 단가
 * @param {number} [options.filters.clientMaxPrice] - 클라이언트 최대 단가
 * @param {boolean} [options.filters.contentOn] - 콘텐츠 보유 여부
 * @returns {Promise} API 응답 (Blob)
 */
export const downloadCSV = async (dataType, options = {}) => {
  const { targetId = null, format = 'data', filters = {} } = options;

  const params = { dataType, format };

  if (targetId) {
    params.targetId = targetId;
  }

  // 필터 파라미터 추가 (값이 있는 것만)
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params[key] = value;
    }
  });

  const response = await apiClient.get('/api/csv-download', {
    params,
    responseType: 'blob',
  });
  return response.data;
};

/**
 * CSV 파일 다운로드 헬퍼 함수
 * Blob 데이터를 파일로 다운로드
 *
 * @param {Blob} blob - CSV Blob 데이터
 * @param {string} filename - 다운로드 파일명
 */
export const downloadCSVFile = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * CSV 다운로드 및 파일 저장 통합 함수
 *
 * @param {CSVDataType} dataType - 데이터 종류
 * @param {string} filename - 다운로드 파일명
 * @param {Object} [options] - 다운로드 옵션
 * @param {string} [options.targetId] - 대상 ID
 * @param {'template' | 'data'} [options.format='data'] - 다운로드 형식
 * @param {Object} [options.filters] - 필터 파라미터
 */
export const downloadAndSaveCSV = async (dataType, filename, options = {}) => {
  const blob = await downloadCSV(dataType, options);
  downloadCSVFile(blob, filename);
};

// ============================================
// 하위 호환성을 위한 deprecated 래퍼 함수들
// 기존 API를 사용하는 코드에서 점진적으로 마이그레이션할 수 있도록 유지
// ============================================

/**
 * @deprecated uploadCSV(file, 'kols-db') 사용을 권장
 */
export const uploadInfluencersCSV = async (file) => {
  console.warn('[DEPRECATED] uploadInfluencersCSV는 deprecated됩니다. uploadCSV(file, "kols-db")를 사용하세요.');
  return uploadCSV(file, 'kols-db');
};

/**
 * @deprecated uploadCSV(file, 'my-kols', id) 사용을 권장
 */
export const uploadMyKolsCsv = async (id, file) => {
  console.warn('[DEPRECATED] uploadMyKolsCsv는 deprecated됩니다. uploadCSV(file, "my-kols", id)를 사용하세요.');
  return uploadCSV(file, 'my-kols', id);
};

/**
 * @deprecated uploadCSV(file, 'shipping-direct', shippingId) 사용을 권장
 */
export const uploadAddressesCSV = async (shippingId, file) => {
  console.warn('[DEPRECATED] uploadAddressesCSV는 deprecated됩니다. uploadCSV(file, "shipping-direct", shippingId)를 사용하세요.');
  return uploadCSV(file, 'shipping-direct', shippingId);
};

/**
 * @deprecated uploadCSV(file, 'campaign-result', campaignId) 사용을 권장
 */
export const uploadResultsCsv = async (campaignId, file) => {
  console.warn('[DEPRECATED] uploadResultsCsv는 deprecated됩니다. uploadCSV(file, "campaign-result", campaignId)를 사용하세요.');
  return uploadCSV(file, 'campaign-result', campaignId);
};

/**
 * @deprecated uploadCSV(file, 'campaign-delivery', campaignId) 사용을 권장
 */
export const uploadDeliveryCSV = async (campaignId, file) => {
  console.warn('[DEPRECATED] uploadDeliveryCSV는 deprecated됩니다. uploadCSV(file, "campaign-delivery", campaignId)를 사용하세요.');
  return uploadCSV(file, 'campaign-delivery', campaignId);
};

/**
 * @deprecated uploadCSV(file, 'campaign-influencer', campaignId) 사용을 권장
 */
export const uploadCampaignInfluencerCSV = async (campaignId, file) => {
  console.warn('[DEPRECATED] uploadCampaignInfluencerCSV는 deprecated됩니다. uploadCSV(file, "campaign-influencer", campaignId)를 사용하세요.');
  return uploadCSV(file, 'campaign-influencer', campaignId);
};
